import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import { Refresh, CloudOff } from '@mui/icons-material';

interface ChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isRetrying: boolean;
  retryCount: number;
}

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  maxRetries?: number;
  retryDelay?: number;
}

class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isRetrying: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ChunkErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);
    
    // Check if this is a chunk loading error
    if (this.isChunkLoadingError(error)) {
      console.log('Chunk loading error detected, will attempt retry');
    }
  }

  private isChunkLoadingError(error: Error): boolean {
    return (
      error.message.includes('Loading chunk') ||
      error.message.includes('ChunkLoadError') ||
      error.message.includes('missing:') ||
      error.message.includes('404') ||
      error.name === 'ChunkLoadError'
    );
  }

  private handleRetry = async () => {
    const { maxRetries = 3, retryDelay = 2000 } = this.props;
    const { retryCount } = this.state;

    if (retryCount >= maxRetries) {
      console.log('Max retries reached, showing manual retry option');
      return;
    }

    this.setState({ isRetrying: true });

    // Clear any existing timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    // Wait before retrying
    this.retryTimeout = setTimeout(() => {
      this.setState(
        (prevState) => ({
          hasError: false,
          isRetrying: false,
          retryCount: prevState.retryCount + 1,
        }),
        () => {
          // Force a page reload to clear any cached chunks
          window.location.reload();
        }
      );
    }, retryDelay);
  };

  private handleManualRetry = () => {
    // Clear browser cache and reload
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName);
        });
      });
    }
    
    // Clear any service worker cache
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }

    // Reload the page
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  render() {
    const { hasError, error, isRetrying, retryCount } = this.state;
    const { maxRetries = 3 } = this.props;

    if (hasError) {
      const isChunkError = error ? this.isChunkLoadingError(error) : false;

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            padding: 2,
          }}
        >
          <Paper
            elevation={2}
            sx={{
              maxWidth: 500,
              width: '100%',
              padding: 3,
              textAlign: 'center',
            }}
          >
            {isRetrying ? (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Retrying...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Attempt {retryCount + 1} of {maxRetries}
                </Typography>
              </Box>
            ) : (
              <>
                <CloudOff sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                
                <Typography variant="h6" gutterBottom>
                  {isChunkError ? 'Failed to Load Component' : 'Something went wrong'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {isChunkError
                    ? 'We encountered an issue loading this page. This might be due to a network problem or cached data.'
                    : error?.message || 'An unexpected error occurred.'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {retryCount < maxRetries && (
                    <Button
                      variant="contained"
                      startIcon={<Refresh />}
                      onClick={this.handleRetry}
                      disabled={isRetrying}
                    >
                      Try Again
                    </Button>
                  )}
                  
                  <Button
                    variant="outlined"
                    onClick={this.handleManualRetry}
                    disabled={isRetrying}
                  >
                    Clear Cache & Reload
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={this.handleGoHome}
                    disabled={isRetrying}
                  >
                    Go Home
                  </Button>
                </Box>

                {process.env.NODE_ENV === 'development' && error && (
                  <Box sx={{ mt: 3, textAlign: 'left' }}>
                    <Typography variant="caption" color="text.secondary">
                      Error: {error.message}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary; 